import { useCallback, useMemo, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { FiltersTypes } from "@common/types/filters/general";
import { TransactionView } from "@common/types/views/transactionViews";
import { View } from "@common/types/views/views";
import { TransactionFilters } from "@common/schemas/transaction.schema";
import { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { TaskType } from "@common/types/task";
import { useScope } from "@state/scopeContext.lib";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { markReviewColumnsRequired } from "@components/tasks/TaskBody/Transactions/injectReviewColumns";
import {
    createDefaultCloseTemplateTransactionReviewView,
    parseTransactionViewConfig,
    readReviewFieldsFromViewConfig,
    serializeTransactionViewConfig,
} from "./closeTemplateTaskViewConfig";
import { TemplatePreviewSwitcher } from "./dataPreview/TemplatePreviewSwitcher";
import { TemplateTransactionsDataTable } from "./dataPreview/TemplateTransactionsDataTable";
import { useTemplateTaskPreview } from "./dataPreview/useTemplateTaskPreview";
import styles from "./CloseTemplateTaskTransactionsPreview.module.scss";

const EMPTY_FILTERS: TransactionFilters = { entityIds: [] };

interface Props {
    taskType: TaskType;
    viewConfig: Record<string, unknown> | null;
    onViewConfigChange: (viewConfig: Record<string, unknown>) => void;
    basis?: CloseTemplateBasis | null;
}

/**
 * Right-panel content for the Add Close Template Task Drawer when task type
 * is TRANSACTIONS.
 *
 * Shows a read-only transactions table with the rows the task would show for
 * the previewed client and entity (see `dataPreview/`), with:
 * - Column management via the existing ColumnSettings component
 * - Filter tabs (date, category, cash/accrual general ledger, amount,
 *   counterparty, class, note, receipts, split, reconciliation, matched,
 *   lock status, status) plus a free-text memo search. The Matched filter's
 *   Invoice/Bill match options are always offered, mirroring the workspace
 *   transactions page — invoice/bill matching works the same way on
 *   cash-basis entities as it does on accrual-ledger ones.
 * - Workspace-scoped templates additionally expose account, ledger group,
 *   exclude from 1099, and review-status filters,
 *   and use the full workspace class/counterparty pickers instead of the
 *   workspace-agnostic toggles
 *
 * The full TransactionView (existing schema) is persisted to `viewConfig`
 * on the task template — no custom schema.
 */
export const CloseTemplateTaskTransactionsPreview: React.FC<Props> = ({
    taskType,
    viewConfig,
    onViewConfigChange,
    basis,
}) => {
    const { workspaceId } = useScope();

    const [view, setView] = useState<TransactionView>(() =>
        parseTransactionViewConfig(viewConfig),
    );

    // Ref to the latest prop so callbacks merge extra top-level fields (e.g.
    // reviewFields) back in without going stale across renders.
    const viewConfigRef = useRef(viewConfig);
    viewConfigRef.current = viewConfig;

    // Re-sync the local view whenever the parent viewConfig changes out of band
    // (e.g. the "Fields to review" selector injects/removes review columns via
    // withReviewFieldsAndColumns). Without this the table would render stale
    // columns and a subsequent reorder/filter would persist them, dropping the
    // just-injected review column.
    const [prevViewConfig, setPrevViewConfig] = useState(viewConfig);
    if (viewConfig !== prevViewConfig) {
        setPrevViewConfig(viewConfig);
        setView(parseTransactionViewConfig(viewConfig));
    }

    const handleViewConfigChange = useCallback(
        (serialized: Record<string, unknown>) => {
            onViewConfigChange({
                ...viewConfigRef.current,
                ...serialized,
            });
        },
        [onViewConfigChange],
    );

    const reviewFields = useMemo(
        () => readReviewFieldsFromViewConfig(viewConfig),
        [viewConfig],
    );

    const { previewTarget, resolvedView } = useTemplateTaskPreview({
        taskType,
        viewConfig: viewConfig ?? serializeTransactionViewConfig(view),
    });

    const baseTransactionsViewConfig = useTransactionViewConfig();

    // Mark review-field columns as required so ColumnSettings prevents
    // hiding them. They can still be pinned and reordered.
    const transactionsViewConfig = useMemo(() => {
        if (reviewFields.length === 0) {
            return baseTransactionsViewConfig;
        }
        const overriddenColumns = markReviewColumnsRequired(
            baseTransactionsViewConfig.columns,
            reviewFields,
        );
        return { ...baseTransactionsViewConfig, columns: overriddenColumns };
    }, [baseTransactionsViewConfig, reviewFields]);

    const defaultViewForSettings = useMemo(
        () =>
            createDefaultCloseTemplateTransactionReviewView(null, reviewFields),
        [reviewFields],
    );

    const handleViewChange = useCallback(
        (updatedView: View) => {
            const next = updatedView as TransactionView;
            setView(next);
            handleViewConfigChange(serializeTransactionViewConfig(next));
        },
        [handleViewConfigChange],
    );

    const viewFilters = useMemo(
        () => (view.filters ?? {}) as TransactionFilters,
        [view.filters],
    );

    const transactionFilters = useMemo(
        (): TransactionFilters => ({
            ...EMPTY_FILTERS,
            ...viewFilters,
        }),
        [viewFilters],
    );

    const handleFilterChange = useCallback(
        (patch: Partial<TransactionFilters>) => {
            setView((prev) => {
                const next: TransactionView = {
                    ...prev,
                    filters: {
                        ...((prev.filters ?? {}) as TransactionFilters),
                        ...patch,
                    },
                };
                handleViewConfigChange(serializeTransactionViewConfig(next));
                return next;
            });
        },
        [handleViewConfigChange],
    );

    const handleFiltersChange = useCallback(
        (patch: Partial<FiltersTypes>) => {
            handleFilterChange({
                ...patch,
                start:
                    patch.start instanceof Date
                        ? parseISO(format(patch.start, "yyyy-MM-dd"))
                        : patch.start,
                end:
                    patch.end instanceof Date
                        ? parseISO(format(patch.end, "yyyy-MM-dd"))
                        : patch.end,
            });
        },
        [handleFilterChange],
    );

    const handleSearchChange = useCallback(
        (search: string) => {
            handleFilterChange({ search: search || undefined });
        },
        [handleFilterChange],
    );

    // When the template is scoped to a workspace, the real class/counterparty/
    // customer lists for that workspace are available, so show the full pickers.
    // Otherwise fall back to the workspace-agnostic presence toggles.
    const classFilterTab = workspaceId
        ? FilterTabs.CLASS
        : FilterTabs.TEMPLATE_CLASS;
    const counterpartyFilterTab = workspaceId
        ? FilterTabs.COUNTERPARTY
        : FilterTabs.TEMPLATE_COUNTERPARTY;
    const customerFilterTab = workspaceId
        ? FilterTabs.CUSTOMER
        : FilterTabs.TEMPLATE_CUSTOMER;

    const availableFilters = [
        FilterTabs.DATE,
        FilterTabs.CATEGORY,
        FilterTabs.CASH_GENERAL_LEDGER,
        ...(basis !== CloseTemplateBasis.MODIFIED_CASH_LEDGER
            ? [FilterTabs.ACCRUAL_GENERAL_LEDGER]
            : []),
        // Financial account list is workspace-specific, so it only makes
        // sense to offer it once the template is scoped to a workspace.
        ...(workspaceId ? [FilterTabs.ACCOUNT] : []),
        FilterTabs.AMOUNT,
        counterpartyFilterTab,
        customerFilterTab,
        classFilterTab,
        // The note filter is a workspace-agnostic memo toggle (has/no/requires
        // memo), mirroring the always-visible memo search box, so it is offered
        // in both org- and workspace-scoped contexts.
        FilterTabs.NOTE,
        FilterTabs.RECEIPTS,
        FilterTabs.SPLIT,
        FilterTabs.RECONCILIATION,
        FilterTabs.MATCHED,
        // Billable invoice status options are a static enum (not
        // workspace-scoped data), so this is offered in both org- and
        // workspace-scoped template contexts.
        FilterTabs.BILLABLE_EXPENSE_INVOICE_STATUSES,
        ...(workspaceId ? [FilterTabs.LEDGER_GROUP] : []),
        FilterTabs.TRANSACTION_LOCKED,
        FilterTabs.STATUS,
        ...(workspaceId ? [FilterTabs.REVIEW] : []),
        ...(workspaceId ? [FilterTabs.EXCLUDE_1099] : []),
    ];

    return (
        <div>
            <TablePageHeader
                className={styles.filters}
                filters={transactionFilters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                onlyStandardCategories
                search={
                    typeof viewFilters.search === "string"
                        ? viewFilters.search
                        : ""
                }
                searchPlaceholder="Filter by memo..."
                onSearchChange={handleSearchChange}
                isFullPage
            >
                <ColumnSettings
                    view={view}
                    viewConfig={transactionsViewConfig}
                    defaultView={defaultViewForSettings}
                    onViewChange={handleViewChange}
                />
                <TemplatePreviewSwitcher target={previewTarget} />
            </TablePageHeader>

            <TemplateTransactionsDataTable
                templateView={view}
                onTemplateViewChange={handleViewChange}
                resolvedView={resolvedView}
                workspaceId={previewTarget.workspaceId}
            />
        </div>
    );
};
